import { RecruitmentCampaignsPanel } from './RecruitmentCampaignsPanel';
import { SetupLinkDialog } from './PracticeSetupLinks';
import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import {
  useClinicalTrialsQueue,
  useUpdateClinicalTrialsPracticeStatus,
  useUpdateClinicalTrialsProfileRequestStatus,
  useUpdateClinicalTrialsSubmissionStatus,
} from '../api/hooks';
import type {
  ClinicalTrialsPracticeSubmission,
  ClinicalTrialsProfileEditRequest,
  ClinicalTrialsProfileStatus,
  ClinicalTrialsPosterStatus,
  ClinicalTrialsPosterSubmission,
} from '../api/types';

type Filter = ClinicalTrialsPosterStatus | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'new', label: 'New' },
  { key: 'reviewed', label: 'Reviewed' },
  { key: 'printed', label: 'Printed' },
  { key: 'sent', label: 'Sent' },
  { key: 'all', label: 'All' },
];

const NEXT_STATUS: Record<ClinicalTrialsPosterStatus, ClinicalTrialsPosterStatus | null> = {
  new: 'reviewed',
  reviewed: 'printed',
  printed: 'sent',
  sent: null,
};

export function ClinicalTrialsPage() {
  const [filter, setFilter] = useState<Filter>('new');
  const { data, isLoading, error } = useClinicalTrialsQueue(filter);
  const updateStatus = useUpdateClinicalTrialsSubmissionStatus();
  const updateProfileStatus = useUpdateClinicalTrialsProfileRequestStatus();
  const updatePracticeStatus = useUpdateClinicalTrialsPracticeStatus();
  const [linkPractice, setLinkPractice] = useState<ClinicalTrialsPracticeSubmission | null>(null);
  const submissions = data?.posterSubmissions ?? [];
  const profileRequests = data?.profileEditRequests ?? [];
  const practiceSubmissions = data?.practiceSubmissions ?? [];

  const moveNext = async (submission: ClinicalTrialsPosterSubmission) => {
    const next = NEXT_STATUS[submission.status];
    if (!next) return;
    try {
      await updateStatus.mutateAsync({ id: submission.id, status: next });
      toast.success(`Marked ${next}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Status update failed');
    }
  };

  const markProfileApplied = async (request: ClinicalTrialsProfileEditRequest) => {
    try {
      await updateProfileStatus.mutateAsync({ id: request.id, status: 'applied' });
      toast.success('Profile request marked applied');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Status update failed');
    }
  };

  const markPracticeApplied = async (practice: ClinicalTrialsPracticeSubmission) => {
    try {
      await updatePracticeStatus.mutateAsync({ id: practice.id, status: 'applied' });
      toast.success('Practice marked processed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Status update failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Clinical Changes"
        description="Poster submissions, profile edits, and practices from the clinical trials app."
        actions={
          <div className="flex gap-1">
            {FILTERS.map(f => (
              <Button
                key={f.key}
                size="sm"
                variant={filter === f.key ? 'default' : 'outline'}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </Button>
            ))}
          </div>
        }
      />

      <RecruitmentCampaignsPanel />
      <QueryState
        isLoading={isLoading}
        error={error}
        empty={
          submissions.length === 0 &&
          profileRequests.length === 0 &&
          practiceSubmissions.length === 0
        }
      >
        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Poster submissions</h2>
            <p className="text-xs text-muted-foreground">
              PDFs and Ivelion team notes submitted from the poster tab.
            </p>
          </div>
          <div className="rounded-lg border">
            {submissions.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">
                No poster submissions for this filter.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Poster</TableHead>
                    <TableHead>Investigator</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {submissions.map(submission => (
                    <TableRow key={submission.id}>
                      <TableCell className="min-w-[18rem]">
                        <div className="flex items-start gap-3">
                          <PosterMini fields={submission.fields} />
                          <div>
                            <div className="font-medium">{submission.fields.header}</div>
                            <div className="mt-1 max-w-[18rem] truncate text-xs text-muted-foreground">
                              {submission.fields.subheader}
                            </div>
                            {submission.notes && (
                              <div className="mt-1 max-w-[18rem] truncate text-xs text-muted-foreground">
                                Ivelion note: {submission.notes}
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {submission.selectedInvestigatorName ??
                            submission.selectedInvestigatorSlug}
                        </div>
                        {submission.selectedInvestigatorEmail && (
                          <div className="text-xs text-muted-foreground">
                            {submission.selectedInvestigatorEmail}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(submission.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={submission.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!submission.fields.exportedPdfDataUrl}
                            onClick={() =>
                              downloadDataUrl(
                                submission.fields.exportedPdfDataUrl!,
                                'Ivelion_poster.pdf'
                              )
                            }
                          >
                            PDF
                          </Button>
                          {NEXT_STATUS[submission.status] && (
                            <Button
                              size="sm"
                              disabled={updateStatus.isPending}
                              onClick={() => void moveNext(submission)}
                            >
                              Mark {NEXT_STATUS[submission.status]}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </section>

        <section className="mt-8 space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Profile edit requests</h2>
            <p className="text-xs text-muted-foreground">
              PI profile changes submitted for review before they are copied to Studien-Netzwerk.
            </p>
          </div>
          <div className="rounded-lg border">
            {profileRequests.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No profile edit requests yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Profile</TableHead>
                    <TableHead>Submitted by</TableHead>
                    <TableHead>Requested changes</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {profileRequests.map(request => (
                    <TableRow key={request.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-full border bg-muted">
                            {request.photoUrl && (
                              <img
                                src={request.photoUrl}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{request.slug}</div>
                            {request.phone && (
                              <div className="text-xs text-muted-foreground">{request.phone}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {request.submittedByName ??
                            request.submittedByEmail ??
                            request.submittedBy}
                        </div>
                        {request.submittedByEmail && (
                          <div className="text-xs text-muted-foreground">
                            {request.submittedByEmail}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="min-w-[22rem]">
                        <ProfileRequestMini request={request} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(request.submittedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <ProfileStatusBadge status={request.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        {request.status !== 'applied' && (
                          <Button
                            size="sm"
                            disabled={updateProfileStatus.isPending}
                            onClick={() => void markProfileApplied(request)}
                          >
                            Mark applied
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </section>

        <section className="mt-8 space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Practice submissions</h2>
            <p className="text-xs text-muted-foreground">
              Outpatient practices submitted for poster production and delivery coordination. A
              setup link connects a Practice's computer to Rudi.
            </p>
          </div>
          <div className="rounded-lg border">
            {practiceSubmissions.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground">No practice submissions yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Practice</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>PI</TableHead>
                    <TableHead>Submitted by</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {practiceSubmissions.map(practice => (
                    <TableRow key={practice.id}>
                      <TableCell className="min-w-[16rem]">
                        <div className="font-medium">{practice.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[practice.address, practice.postalCode, practice.city]
                            .filter(Boolean)
                            .join(', ') || 'No address'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <FieldLine label="Name" value={practice.contactName} />
                        <FieldLine label="Email" value={practice.contactEmail} />
                        <FieldLine label="Phone" value={practice.contactPhone} />
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {practice.selectedInvestigatorName ??
                            practice.selectedInvestigatorSlug ??
                            'Not assigned'}
                        </div>
                        {practice.selectedInvestigatorEmail && (
                          <div className="text-xs text-muted-foreground">
                            {practice.selectedInvestigatorEmail}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {practice.submittedByName ??
                            practice.submittedByEmail ??
                            practice.submittedBy}
                        </div>
                        {practice.submittedByEmail && (
                          <div className="text-xs text-muted-foreground">
                            {practice.submittedByEmail}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(practice.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <ProfileStatusBadge status={practice.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            aria-label={`Setup link for ${practice.name}`}
                            onClick={() => setLinkPractice(practice)}
                          >
                            Setup link
                          </Button>
                          {practice.status !== 'applied' && (
                            <Button
                              size="sm"
                              disabled={updatePracticeStatus.isPending}
                              onClick={() => void markPracticeApplied(practice)}
                            >
                              Mark processed
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </section>
      </QueryState>
      {linkPractice && (
        <SetupLinkDialog
          key={linkPractice.id}
          practice={{
            id: linkPractice.id,
            name: linkPractice.name,
            tenantId: linkPractice.tenantId,
            teamId: linkPractice.teamId ?? null,
            email: linkPractice.contactEmail,
          }}
          open
          onClose={() => setLinkPractice(null)}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ClinicalTrialsPosterStatus }) {
  if (status === 'new') return <Badge variant="outline">new</Badge>;
  if (status === 'sent') return <Badge>sent</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function ProfileStatusBadge({ status }: { status: ClinicalTrialsProfileStatus }) {
  if (status === 'applied') return <Badge>applied</Badge>;
  return <Badge variant="outline">pending</Badge>;
}

function ProfileRequestMini({ request }: { request: ClinicalTrialsProfileEditRequest }) {
  return (
    <div className="space-y-1 text-xs">
      <FieldLine label="Bio" value={request.bio} multiline />
      <FieldLine label="Arbeitsgruppe" value={request.groupName} />
      <FieldLine label="Beschreibung" value={request.groupDescription} multiline />
      <FieldLine label="Telefon" value={request.phone} />
      {request.additionalPhones && (
        <FieldLine label="Weitere Nummern" value={request.additionalPhones.join(', ') || '—'} />
      )}
      {request.clinicRefs && (
        <FieldLine
          label="Kliniken"
          value={request.clinicRefs.map(clinic => clinic.name).join(', ') || '—'}
        />
      )}
    </div>
  );
}

function FieldLine({
  label,
  value,
  multiline = false,
}: {
  label: string;
  value: string | null | undefined;
  multiline?: boolean;
}) {
  return (
    <div className="grid grid-cols-[7rem_1fr] gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={multiline ? 'line-clamp-2' : 'truncate'}>{value || '—'}</span>
    </div>
  );
}

function PosterMini({ fields }: { fields: ClinicalTrialsPosterSubmission['fields'] }) {
  return (
    <div className="relative h-24 w-16 shrink-0 overflow-hidden rounded border bg-white p-1">
      <div className="mt-4 rounded bg-neutral-100 p-1">
        <div className="line-clamp-2 text-[7px] font-semibold leading-tight text-neutral-950">
          {fields.header}
        </div>
        <div className="mt-1 line-clamp-2 text-[5px] leading-tight text-neutral-700">
          {fields.subheader}
        </div>
      </div>
      <div className="absolute bottom-1 left-1 right-1 flex gap-1">
        <div className="h-4 w-5 overflow-hidden rounded bg-neutral-100">
          {fields.bottomImageDataUrl && (
            <img src={fields.bottomImageDataUrl} alt="" className="h-full w-full object-cover" />
          )}
        </div>
        <div className="line-clamp-2 flex-1 text-[5px] leading-tight">{fields.bottomText}</div>
      </div>
    </div>
  );
}

function downloadDataUrl(dataUrl: string, filename: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}
